import api from './api';
import { Sale, SaleType, PaymentMethod, SaleStatus } from '@/types';
import { Unit } from '@/utils/units';

export interface SaleItemCreate {
  product_id?: number;  // Opcional para items personalizados
  custom_name?: string; // Nombre para items sin producto
  quantity?: number;        // En quantity_unit (lo que ingreso el usuario)
  quantity_unit?: Unit;     // Unidad en la que viene la quantity (default = display_unit del producto)
  unit_price?: number;      // $ por base_unit
  discount_percentage?: number;
  discount_amount?: number;
  notes?: string;
}

export interface SaleCreate {
  sale_type?: SaleType;
  customer_name?: string;
  customer_phone?: string;
  customer_document?: string;
  items: SaleItemCreate[];
  discount_percentage?: number;
  discount_amount?: number;
  payment_method?: PaymentMethod;
  payment_reference?: string;
  notes?: string;
  discount_reason_id?: number;
  // Comprobante sin venta: se entrega el ticket pero la plata entra por otra via.
  // Queda en el listado pero fuera de caja, totales, ganancia y puntos.
  is_placeholder?: boolean;
  // Pago dividido (trackeo interno; el cajero ingresa los montos a mano)
  is_split?: boolean;
  amount_cash?: number;
  amount_card?: number;
  amount_transfer?: number;
  amount_qr?: number;
  // Puntos VIP: canjear 1.000 pts (= $30.000) del cliente identificado por DNI
  loyalty_redeem?: boolean;
}

export interface SaleFilters {
  sale_type?: SaleType;
  sale_status?: SaleStatus;
  payment_method?: PaymentMethod;
  date_from?: string;
  date_to?: string;
  skip?: number;
  limit?: number;
}

export const salesService = {
  async getSales(filters?: SaleFilters): Promise<Sale[]> {
    const response = await api.get<Sale[]>('/sales', { params: filters });
    return response.data;
  },

  async getSale(id: number): Promise<Sale> {
    const response = await api.get<Sale>(`/sales/${id}`);
    return response.data;
  },

  async createSale(data: SaleCreate): Promise<Sale> {
    const response = await api.post<Sale>('/sales', data);
    return response.data;
  },

  async cancelSale(id: number): Promise<Sale> {
    const response = await api.post<Sale>(`/sales/${id}/cancel`);
    return response.data;
  },

  // Guarda el N° de comprobante fiscal emitido (para poder reimprimirlo luego)
  async setFiscalComprobante(id: number, numero: string): Promise<Sale> {
    const response = await api.post<Sale>(`/sales/${id}/fiscal-comprobante`, null, { params: { numero } });
    return response.data;
  },

  // Asigna un DNI a una venta ya hecha y acredita los puntos VIP retroactivamente
  async assignLoyaltyDni(id: number, dni: string): Promise<Sale> {
    const response = await api.post<Sale>(`/sales/${id}/loyalty-dni`, null, { params: { dni } });
    return response.data;
  },

  // Edita una venta ya hecha (solo admin): medio de pago + descuento
  async editSale(id: number, data: SaleUpdate): Promise<Sale> {
    const response = await api.patch<Sale>(`/sales/${id}`, data);
    return response.data;
  },

  // Emite la Factura C electrónica por ARCA (WSFE). Devuelve la venta con CAE + QR.
  async emitElectronicInvoice(id: number): Promise<Sale> {
    const response = await api.post<Sale>(`/sales/${id}/factura-electronica`);
    return response.data;
  },
};

export interface SaleUpdate {
  reason?: string;
  payment_method?: PaymentMethod;
  payment_reference?: string;
  discount_percentage?: number;
  discount_amount?: number;
  // Motivo del descuento: obligatorio cuando hay descuento.
  discount_reason_id?: number;
  is_placeholder?: boolean;
  is_split?: boolean;
  amount_cash?: number;
  amount_card?: number;
  amount_transfer?: number;
  amount_qr?: number;
}
